import { useState } from "react";
import { X } from "lucide-react";

import type { Run } from "@/lib/integrations/api";
import { columnChart, outcomeTotals } from "@/lib/integrations/runsChart";
import { outcomeCounts, runStatus, viaWords, when, type Tone } from "@/lib/integrations/words";

import { TONE_FILL } from "./HealthPill";
import styles from "./integrations.module.css";

const FILL: Record<Tone, string> = {
  good: "var(--good)",
  info: "var(--accent)",
  warning: "var(--warn)",
  danger: "var(--bad)",
  neutral: "var(--ink-3)",
};

const AXIS = 44;
const TOP = 10;
const BOTTOM = 22;
const PITCH = 30;
const BAR = 18;
const HEIGHT = 112;

function tick(value: number, max: number): string {
  if (max >= 10000) return value === 0 ? "0" : `${Number((value / 1000).toFixed(1))}k`;
  return value.toLocaleString("en-GB");
}

function day(moment: string | null, timeZone: string): string {
  if (!moment) return "";
  try {
    return new Date(moment).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone });
  } catch {
    return new Date(moment).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  }
}

/** A column with only its top corners rounded, square on the baseline. */
function topRounded(x: number, y: number, width: number, height: number): string {
  const r = Math.min(3, height / 2, width / 2);
  return `M${x},${y + height}V${y + r}Q${x},${y} ${x + r},${y}H${x + width - r}Q${x + width},${y} ${x + width},${y + r}V${y + height}Z`;
}

/**
 * The last runs as columns: height is the rows Scout sent, split by what
 * happened to them. Hover or focus a column for its details; click or press
 * Enter to open it. The legend beside it carries every word and count.
 */
export function RunsChart({ runs, timeZone, onOpen }: { runs: Run[]; timeZone: string; onOpen: (run: Run) => void }) {
  const [hover, setHover] = useState<number | null>(null);
  const chart = columnChart(runs, { height: HEIGHT, pitch: PITCH, barWidth: BAR });
  const totals = outcomeTotals(runs);
  const failed = runs.filter((run) => runStatus(run.status).tone === "danger").length;
  const width = AXIS + chart.width + 2;
  const base = TOP + HEIGHT;
  const shown = hover === null ? null : runs[hover];
  const flip = hover !== null && chart.width > 300 && hover >= runs.length / 2;

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartPlot} style={{ width }}>
        <svg width={width} height={base + BOTTOM} className={styles.chartSvg} role="group" aria-label={`The last ${runs.length} runs, oldest on the left`}>
          {chart.ticks.map((value) => {
            const y = base - (value / chart.max) * HEIGHT;
            return (
              <g key={value} aria-hidden>
                <line x1={AXIS} x2={width} y1={y} y2={y} className={value === 0 ? styles.chartBase : styles.chartGrid} />
                <text x={AXIS - 8} y={y} dy="0.32em" textAnchor="end" className={styles.chartAxis}>
                  {tick(value, chart.max)}
                </text>
              </g>
            );
          })}
          {chart.bars.map((bar, index) => {
            const run = runs[index];
            const status = runStatus(run.status);
            const parts = outcomeCounts(run.counts);
            const x = AXIS + bar.x;
            const label = `${when(run.createdAt, timeZone)}. ${status.word}. ${run.rowsCollected.toLocaleString()} rows${parts.length ? `: ${parts.map((part) => `${part.word} ${part.count.toLocaleString()}`).join(", ")}` : ""}. Opens the run.`;
            return (
              <g
                key={run.id}
                className={styles.chartBar}
                tabIndex={0}
                role="button"
                aria-label={label}
                onMouseEnter={() => setHover(index)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(index)}
                onBlur={() => setHover(null)}
                onClick={() => onOpen(run)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpen(run);
                  }
                }}
              >
                <rect x={AXIS + index * PITCH} y={TOP - 4} width={PITCH} height={HEIGHT + 4} rx={4} className={styles.chartHit} />
                {bar.segments.length === 0 && bar.height > 0 && (
                  <path d={topRounded(x, TOP + bar.top, BAR, bar.height)} fill={FILL.neutral} />
                )}
                {bar.segments.map((segment, i) =>
                  i === bar.segments.length - 1 ? (
                    <path key={segment.key} d={topRounded(x, TOP + segment.y, BAR, segment.height)} fill={FILL[segment.tone]} />
                  ) : (
                    <rect key={segment.key} x={x} y={TOP + segment.y} width={BAR} height={segment.height} fill={FILL[segment.tone]} />
                  ),
                )}
                {status.tone === "danger" && (
                  <g transform={`translate(${x + BAR / 2}, ${TOP + bar.top - 9})`} className={styles.chartFail}>
                    <circle r={6} />
                    <path d="M-2.5,-2.5L2.5,2.5M2.5,-2.5L-2.5,2.5" />
                  </g>
                )}
              </g>
            );
          })}
          {runs.length > 0 && (
            <g aria-hidden className={styles.chartAxis}>
              <text x={AXIS + chart.bars[0].x} y={base + 16} textAnchor="start">
                {day(runs[0].createdAt, timeZone)}
              </text>
              {runs.length > 3 && day(runs[runs.length - 1].createdAt, timeZone) !== day(runs[0].createdAt, timeZone) && (
                <text x={AXIS + chart.bars[runs.length - 1].x + BAR} y={base + 16} textAnchor="end">
                  {day(runs[runs.length - 1].createdAt, timeZone)}
                </text>
              )}
            </g>
          )}
        </svg>
        {shown && hover !== null && (
          <div
            className={styles.chartTip}
            style={{ left: AXIS + hover * PITCH + (flip ? 0 : PITCH + 6), top: 0, transform: flip ? "translateX(calc(-100% - 6px))" : undefined }}
            aria-hidden
          >
            <strong>{when(shown.createdAt, timeZone)}</strong>
            <span className={styles.faint}>
              {runStatus(shown.status).word} · {viaWords(shown.via)}
            </span>
            <span>{shown.rowsCollected.toLocaleString()} rows from Scout</span>
            {outcomeCounts(shown.counts).length > 0 && (
              <ul className={styles.legendList}>
                {outcomeCounts(shown.counts).map((part) => (
                  <li key={part.key} className={styles.legendRow}>
                    <span className={`${styles.swatch} ${TONE_FILL[part.tone]}`} aria-hidden />
                    <span>{part.word}</span>
                    <span className={styles.legendCount}>{part.count.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      <div className={styles.chartSide}>
        <p className={styles.chartSummary}>
          <strong>
            {runs.length} {runs.length === 1 ? "run" : "runs"}
          </strong>{" "}
          <span className={styles.faint}>· rows by what happened to them, over all shown runs</span>
        </p>
        <ul className={styles.legendList}>
          {totals.map((part) => (
            <li key={part.key} className={styles.legendRow}>
              <span className={`${styles.swatch} ${TONE_FILL[part.tone]}`} aria-hidden />
              <span>{part.word}</span>
              <span className={styles.legendCount}>{part.count.toLocaleString()}</span>
            </li>
          ))}
          {totals.length === 0 && <li className={styles.faint}>No rows in these runs.</li>}
          {failed > 0 && (
            <li className={styles.legendRow}>
              <span className={styles.legendFail} aria-hidden>
                <X size={9} />
              </span>
              <span>Failed or paused runs</span>
              <span className={styles.legendCount}>{failed}</span>
            </li>
          )}
        </ul>
        <p className={styles.faint}>Oldest on the left. Height is the rows Scout sent. Point at a column for details; click it to open the run.</p>
      </div>
    </div>
  );
}
